import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import type { ReviewCycle, ReviewComment, User } from './types';
import './ContentReview.css';
import { openComment } from './openComment';
import {
  CommentAnchorApi, CommentAnchorProvider, flashAnchor, applyActiveAnchor, applyHoverAnchor,
  idsOnElement, READING_BAND
} from './commentAnchors';
import { markerMentionProps } from './mentions';
import CommentComposer, { ComposerSubmission } from './CommentComposer';
import ViewerSidePanel from './ViewerSidePanel';

interface Coordinates {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

interface SnapshotViewerProps {
  user: User | null;
  cycle: ReviewCycle;
  comments: ReviewComment[];
  getAuthHeaders: () => Promise<Record<string, string>>;
  onCommentAdded: () => void;
  readOnly?: boolean;
  /** Threaded through from DocumentViewer so the consultations tab re-reads
      on every parent refetch. REQUIRED there; optional here only because the
      snapshot path polls its own comments. */
  refreshKey?: string | number;
  /** Decision-maker override for editing someone else's comment. See the
      DocumentViewer prop of the same name — it was `!readOnly` here too. */
  canEditAnyComment?: boolean;
  /** See DocumentViewer — the rail owns the list, the host owns the card. */
  renderCommentDetail?: (comment: ReviewComment) => React.ReactNode;
  commentsHeader?: React.ReactNode;
  // 'reply-only' (2026-08-24) — the named producer. They read everything and
  // reply in threads others started, but cannot open a new one, so the whole
  // authoring surface goes: no "Add comment", no pins, no composer. Distinct
  // from readOnly, which also changes which comments are listed.
  composerMode?: 'full' | 'consultation' | 'reply-only';
  commentVisibility?: 'mine' | 'all' | 'others';
  /** Polling interval (ms) while snapshotStatus === 'pending'. */
  pollIntervalMs?: number;
  onStatusChange?: (status: ReviewCycle['snapshotStatus']) => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * The width the snapshot was CAPTURED at — webPageScraperService.js calls
 * `page.setViewport({ width: 1440, height: 900 })` for both the capture and
 * the re-capture path. Change one and you must change the other.
 *
 * WHY THE IFRAME IS PINNED TO IT (2026-08-26, Fredrik)
 *
 * Marker coordinates are raw CSS pixels in the snapshot document's own
 * layout: a drag is stored as `dragRect + iframeScroll`, with no width
 * normalisation anywhere. The iframe used to render at `width: 100%` of a
 * column whose width depends on the browser window, whether the right rail is
 * present, and now whether it is collapsed — so a responsive snapshot
 * reflowed underneath saved markers and they pointed at the wrong element.
 * That was already true before the rail became collapsible: a marker placed
 * on a 1600px screen did not land in the same place on a 1280px one.
 *
 * The stage is therefore always 1440 wide — the one width at which stored
 * coordinates are correct — and CSS-scaled to fit whatever space the column
 * has. Markers, the drag overlay and the in-progress rectangle all live
 * INSIDE the scaled stage, so they scale with the page and need no per-marker
 * conversion. Only pointer input crosses the boundary, and it divides by the
 * scale (see handleOverlayMouseDown).
 */
const SNAPSHOT_CAPTURE_WIDTH = 1440;

/**
 * ...and the height, from the same setViewport call.
 *
 * The stage was first given a height that GREW as it scaled down, so the
 * on-screen box stayed a constant size and a zoomed-out reviewer saw more of
 * the page. Measured against a real snapshot, that reintroduced the same bug
 * on the other axis: the iframe's viewport height is part of the layout, so
 * anything sized in vh, vertically centred, or sticky moved when the window
 * changed width. A marker's stored coordinates stayed put and the CONTENT
 * slid under them.
 *
 * Both axes are pinned to the capture viewport now. The iframe lays out
 * exactly as the snapshot was taken, at every window size, and the whole
 * stage is scaled to fit. The cost is a shorter viewer box on a narrow
 * window — the page scrolls inside the iframe, as it always has.
 */
const SNAPSHOT_CAPTURE_HEIGHT = 900;

/** The presentation modal currently open in the snapshot, and which slide. */
interface LiveSlide {
  widget: string;
  index: number;
  /** Mounted slide viewport, iframe VIEWPORT coordinates (the modal is fixed). */
  rect: { x: number; y: number; width: number; height: number };
  /** The card the modal opened from, snapshot DOCUMENT coordinates. */
  cardRect: { x: number; y: number; width: number; height: number };
}

/**
 * Read the open media-cards presentation from the snapshot DOM: the modal
 * with `is-open`, the card it was opened from (the modal shows the card's
 * title — that is the only link the widget leaves), and the active slide
 * (the active dot, else the track's translateX). Null when nothing is open.
 */
function readLiveSlide(doc: Document): LiveSlide | null {
  const modal = doc.querySelector('[class*="__modal"].is-open');
  if (!modal) return null;
  const root = modal.closest('[data-media-cards]');
  const viewport = modal.querySelector('.mc-carousel__viewport');
  if (!root || !viewport) return null;
  const title = (modal.querySelector('[id^="mc-title-"]')?.textContent || '').trim();
  const card = Array.from(root.querySelectorAll<HTMLElement>('[data-card-variant="carousel"]'))
    .find(el => el.id && (el.dataset.carouselTitle || '').trim() === title);
  if (!card) return null;
  const dot = modal.querySelector('[data-carousel-dot].is-active');
  let index = dot ? parseInt(dot.getAttribute('data-carousel-dot') || '', 10) : NaN;
  if (Number.isNaN(index)) {
    const track = modal.querySelector<HTMLElement>('[data-carousel-track]');
    const m = /translateX\((-?[\d.]+)%\)/.exec(track?.style.transform || '');
    index = m ? Math.round(-parseFloat(m[1]) / 100) : 0;
  }
  const r = viewport.getBoundingClientRect();
  const cr = card.getBoundingClientRect();
  const win = doc.defaultView;
  return {
    widget: card.id, index,
    rect: { x: r.left, y: r.top, width: r.width, height: r.height },
    cardRect: { x: cr.left + (win?.scrollX || 0), y: cr.top + (win?.scrollY || 0), width: cr.width, height: cr.height }
  };
}

/**
 * Does this comment belong on the slide that is showing? Three cases:
 * a slide comment — only its own slide; a slide comment whose slide is gone
 * from the live deck (index -1) — never inside the modal; a comment on the
 * card with no slide at all — about the whole widget, so every slide of it.
 */
function isOnLiveSlide(c: ReviewComment, live: LiveSlide): boolean {
  const co = c.coordinates;
  if (!co) return false;
  if (co.slide) return co.slide.widget === live.widget && co.slide.index === live.index;
  const w = co.width || 0, h = co.height || 0;
  const r = live.cardRect;
  return co.x < r.x + r.width && co.x + w > r.x && co.y < r.y + r.height && co.y + h > r.y;
}

/**
 * Phase 11 — renders a self-contained HTML snapshot of a web page in a
 * sandboxed iframe and lets reviewers comment by drawing rectangles or
 * dropping pins anchored in iframe-content coordinates.
 *
 * Sandbox attributes:
 *   - allow-same-origin: lets inlined CSS/JS apply, and lets the parent reach
 *     into iframe.contentDocument for section-context extraction at comment
 *     time and for scroll tracking
 *   - allow-scripts: needed for inlined scripts (carousel libs, etc.) to run
 *   - top-level navigation NOT allowed
 *
 * UX: by default the iframe is interactive — reviewers scroll, expand
 * accordions, click into tabs. Hitting "Add comment" enters comment mode: an
 * overlay covers the iframe and intercepts a rectangle drag. After commit
 * (or cancel) the overlay disappears and the iframe is interactive again.
 */

// ---------------------------------------------------------------------------
// Pure helpers — module scope so useCallback deps stay stable.
// ---------------------------------------------------------------------------

// Read the visible rendered text from an element. Uses innerText so that
// inlined <style> / <script> tags (single-file inlines all CSS into the
// document body) and hidden elements are excluded.
const visibleText = (el: Element): string => {
  const raw = (el as HTMLElement).innerText ?? '';
  return raw.replace(/\s+/g, ' ').trim();
};

// Format an <img> / <picture> as a one-line text descriptor for inclusion in
// the captured section context. Prefers alt text, falls back to filename.
const describeImage = (el: Element): string => {
  let img: HTMLImageElement | null = null;
  if (el.tagName.toLowerCase() === 'img') img = el as HTMLImageElement;
  else if (el.tagName.toLowerCase() === 'picture') img = el.querySelector('img');
  if (!img) return '[Image]';
  const alt = (img.alt || '').trim();
  const src = img.currentSrc || img.src || '';
  let filename = '';
  if (src && !src.startsWith('data:')) {
    filename = src.split('/').pop()?.split('?')[0]?.split(';')[0] || '';
  }
  if (alt && filename) return `[Image: ${filename} — "${alt}"]`;
  if (alt) return `[Image: "${alt}"]`;
  if (filename) return `[Image: ${filename}]`;
  return '[Image]';
};

// Walk up to the SMALLEST block that actually contains the click — a
// paragraph / heading / list-item / small div. We deliberately bail out
// before reaching <section> / <article> / <main> because innerText on those
// returns the WHOLE section (starting from its heading at the top), which
// shows up to the reviewer as "text from a couple of sections above".
const findBlockAncestor = (el: Element): Element | null => {
  const SKIP = new Set(['style', 'script', 'noscript', 'template', 'link', 'meta', 'br', 'svg', 'path']);
  const STOP = new Set(['section', 'article', 'main', 'body', 'html']);
  let cur: Element | null = el;
  let depth = 0;
  while (cur && depth < 25) {
    const tag = cur.tagName.toLowerCase();
    if (SKIP.has(tag)) { cur = cur.parentElement; depth++; continue; }
    if (STOP.has(tag)) return null; // too coarse — drop this sample point

    // <img> / <picture> are leaf-ish — accept them so we can describe them
    if (tag === 'img' || tag === 'picture') return cur;

    const text = visibleText(cur);
    if (!text) { cur = cur.parentElement; depth++; continue; }

    // Small, precise blocks — return immediately
    if (/^h[1-6]$/.test(tag)) return cur;
    if (tag === 'p' || tag === 'li' || tag === 'blockquote' || tag === 'figcaption') return cur;
    if (tag === 'td' || tag === 'th' || tag === 'caption') return cur;
    if (tag === 'button') return cur;
    if (tag === 'a' && text.length <= 300) return cur;

    // div: only when it looks like a text card / paragraph-equivalent
    if (tag === 'div' && text.length >= 20 && text.length <= 400) return cur;
    // span: only when it has its own substantial-but-bounded text
    if (tag === 'span' && text.length >= 15 && text.length <= 300) return cur;

    cur = cur.parentElement;
    depth++;
  }
  return null;
};

// Resolve a caret (text-node + offset) at a viewport point. Uses the modern
// caretPositionFromPoint with a fallback to caretRangeFromPoint for older
// Chromium / WebKit.
const caretAt = (doc: Document, x: number, y: number): { node: Node; offset: number } | null => {
  const anyDoc = doc as any;
  if (typeof anyDoc.caretPositionFromPoint === 'function') {
    const p = anyDoc.caretPositionFromPoint(x, y);
    if (p && p.offsetNode) return { node: p.offsetNode, offset: p.offset };
  }
  if (typeof anyDoc.caretRangeFromPoint === 'function') {
    const r = anyDoc.caretRangeFromPoint(x, y);
    if (r) return { node: r.startContainer, offset: r.startOffset };
  }
  return null;
};

// Shrink a Range so every included character's bounding rect lies within
// the dragged rectangle. Trims one character at a time from each end as
// long as that character's rect falls outside (with a small tolerance for
// line-leading / sub-pixel rounding). Bounded by a hard iteration cap so a
// weird DOM can't lock the UI.
const trimRangeToRect = (
  doc: Document,
  range: Range,
  rect: { vx: number; vy: number; width: number; height: number }
): void => {
  const tolerance = 2;
  const top = rect.vy;
  const bottom = rect.vy + rect.height;
  const MAX_STEPS = 200;

  const probeRect = (node: Node, start: number, end: number): DOMRect | null => {
    try {
      const probe = doc.createRange();
      probe.setStart(node, start);
      probe.setEnd(node, end);
      return probe.getBoundingClientRect();
    } catch {
      return null;
    }
  };

  // Trim end: while the last char's TOP is below the rectangle's bottom,
  // shrink the end by one. Stops at offset 0 (can't easily walk to the
  // previous text node without a TreeWalker, and the symptom is almost
  // always within a single text node).
  for (let i = 0; i < MAX_STEPS; i++) {
    const endNode = range.endContainer;
    const endOffset = range.endOffset;
    if (endNode.nodeType !== Node.TEXT_NODE || endOffset <= 0) break;
    const r = probeRect(endNode, endOffset - 1, endOffset);
    if (!r) break;
    const isEmpty = r.width === 0 && r.height === 0;
    if (!isEmpty && r.top <= bottom + tolerance) break;
    range.setEnd(endNode, endOffset - 1);
    if (range.collapsed) break;
  }

  // Trim start: while the first char's BOTTOM is above the rectangle's top,
  // shrink the start by one.
  for (let i = 0; i < MAX_STEPS; i++) {
    const startNode = range.startContainer;
    const startOffset = range.startOffset;
    if (startNode.nodeType !== Node.TEXT_NODE) break;
    const len = (startNode as Text).length;
    if (startOffset >= len) break;
    const r = probeRect(startNode, startOffset, startOffset + 1);
    if (!r) break;
    const isEmpty = r.width === 0 && r.height === 0;
    if (!isEmpty && r.bottom >= top - tolerance) break;
    range.setStart(startNode, startOffset + 1);
    if (range.collapsed) break;
  }
};

const SnapshotViewer: React.FC<SnapshotViewerProps> = ({
  user, cycle, comments, getAuthHeaders, onCommentAdded,
  readOnly = false, composerMode = 'full', commentVisibility, pollIntervalMs = 5000, onStatusChange,
  refreshKey = 0, canEditAnyComment = false, renderCommentDetail, commentsHeader
}) => {
  // Can this viewer START something here? Replies are not authored in the
  // viewer at all (they live in the Comment Summary), so switching this off
  // removes thread-starting without touching the producer's ability to answer.
  //
  // Note what this deliberately does NOT change: showRightColumn. The right
  // column stays reserved even when nobody can author, because the iframe
  // width is what saved marker coordinates were measured against — narrowing
  // it reflows the page and drifts every existing marker.
  const canAuthor = !readOnly && composerMode !== 'reply-only';
  // Phase 11.1 state
  const [snapshotBlobUrl, setSnapshotBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polledStatus, setPolledStatus] = useState<ReviewCycle['snapshotStatus']>(cycle.snapshotStatus);
  // Bumped by the Retry button to re-run the load effect below.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Phase 11.2 state
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeScroll, setIframeScroll] = useState({ x: 0, y: 0 });
  const [contentDocAccessible, setContentDocAccessible] = useState<boolean>(true);
  // The presentation the reader has OPEN in the snapshot, read from its live
  // DOM (2026-09-04, row:2 walk). A media-cards modal is a fixed overlay, so a
  // marker stored as page coordinates floats over every slide of it; comments
  // that carry `coordinates.slide` are drawn over the mounted carousel only
  // while their slide is showing, and everything else is hidden while a modal
  // is up (it is behind the modal anyway). `rect` is in iframe VIEWPORT
  // coordinates — fixed elements do not move with the page scroll.
  const [liveSlide, setLiveSlide] = useState<LiveSlide | null>(null);
  const [commentMode, setCommentMode] = useState(false);
  const [dragStart, setDragStart] = useState<{ vx: number; vy: number } | null>(null);
  const [dragRect, setDragRect] = useState<{ vx: number; vy: number; width: number; height: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Final pending comment (in CONTENT coords, i.e. anchored to iframe document)
  const [pendingCoords, setPendingCoords] = useState<Coordinates | null>(null);
  const [pendingSection, setPendingSection] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // A refused write used to go to console.error only, so the composer just sat
  // there — Fredrik hit exactly that as a producer, whose findings the server
  // refuses (2026-08-21). Say what happened.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hoveredComments, setHoveredComments] = useState<{ comments: ReviewComment[]; x: number; y: number } | null>(null);
  /**
   * Which marker the pointer is over, for the rail (2026-08-27, Fredrik).
   *
   * Separate from `hoveredComments` above, which drives the popover and
   * carries a CLUSTER plus a position. The rail wants one id and nothing
   * else, and folding the two together would tie the rail's highlight to
   * whether a popover happens to be showing.
   */
  const [hoveredAnchorId, setHoveredAnchorId] = useState<string | null>(null);
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayMarkerRef = useRef<HTMLDivElement>(null);

  // Scale factor applied to the fixed 1440x900 stage.
  const [stageScale, setStageScale] = useState(1);

  // ────────────────────────── Phase 11.1 effects ──────────────────────────

  // Keep the local status in step with the prop when the parent refetches.
  useEffect(() => {
    setPolledStatus(cycle.snapshotStatus);
  }, [cycle.snapshotStatus]);

  // Poll the cycle endpoint while pending.
  //
  // 2026-08-19: this used to key off cycle.snapshotStatus — the PROP — so it
  // only ever ran when the viewer MOUNTED in 'pending'. The self-heal path
  // arrives the other way round: the row says 'ready', the file is gone, and
  // the GET below flips the row to 'pending' server-side and 404s. The prop
  // never changes, so nothing polled and the viewer sat on "Couldn't load
  // snapshot" until the user reloaded the page by hand. Keying on the LOCAL
  // status lets the failed load start the poll itself.
  useEffect(() => {
    if (polledStatus !== 'pending') return;

    let cancelled = false;
    const tick = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycle._id}`, { headers });
        if (cancelled) return;
        const next = res.data?.cycle?.snapshotStatus;
        if (next && next !== 'pending') {
          setPolledStatus(next);
          onStatusChange?.(next);
          return;
        }
      } catch {
        // transient — keep polling
      }
      if (!cancelled) setTimeout(tick, pollIntervalMs);
    };
    setTimeout(tick, pollIntervalMs);
    return () => { cancelled = true; };
  }, [cycle._id, polledStatus, getAuthHeaders, pollIntervalMs, onStatusChange]);

  // Load the snapshot HTML once status is ready
  useEffect(() => {
    if (polledStatus !== 'ready') {
      setSnapshotBlobUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(
          `${backendUrl}/api/content-review/cycles/${cycle._id}/snapshot`,
          { headers, responseType: 'blob' }
        );
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'text/html' });
        objectUrl = URL.createObjectURL(blob);
        setSnapshotBlobUrl(objectUrl);
        setError(null);
      } catch (err: any) {
        // The bytes were cleared (historically by a deploy — uploads/ was
        // ephemeral until the disk landed 2026-08-19). The backend has already
        // kicked off a re-capture and flipped the row to 'pending'; say so and
        // start polling rather than showing a dead end the user has to reload
        // out of.
        if (err.response?.data?.code === 'SNAPSHOT_RECAPTURING') {
          if (!cancelled) {
            setError(null);
            setPolledStatus('pending');
          }
          return;
        }
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load snapshot');
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [polledStatus, cycle._id, getAuthHeaders, reloadNonce]);

  // ────────────────────────── Phase 11.2 effects ──────────────────────────

  // Attach scroll listener to iframe contentWindow once it's loaded.
  // Tracks scroll so saved comment markers can be re-positioned in viewport coords.
  useEffect(() => {
    if (!iframeReady) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let win: Window | null = null;
    try {
      win = iframe.contentWindow;
      if (!win) return;
      // Touch contentDocument once to detect cross-origin block early
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      iframe.contentDocument;
      setContentDocAccessible(true);
    } catch {
      setContentDocAccessible(false);
      return;
    }

    const handleScroll = () => {
      if (!win) return;
      setIframeScroll({ x: win.scrollX || 0, y: win.scrollY || 0 });
    };

    try {
      win.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      return () => { try { win!.removeEventListener('scroll', handleScroll); } catch {} };
    } catch {
      setContentDocAccessible(false);
    }
  }, [iframeReady, snapshotBlobUrl]);

  // Watch the snapshot's DOM for a presentation modal opening, closing or
  // changing slide. The page's own scripts drive all three (class toggles
  // and a transform on the track), so a MutationObserver on attributes is
  // the whole signal; resize re-measures the mounted slide's rectangle.
  useEffect(() => {
    if (!iframeReady || !contentDocAccessible) return;
    const doc = iframeRef.current?.contentDocument;
    const win = iframeRef.current?.contentWindow;
    if (!doc || !win) return;
    let last = '';
    const sync = () => {
      const next = readLiveSlide(doc);
      const key = next ? `${next.widget}|${next.index}|${Math.round(next.rect.x)}|${Math.round(next.rect.y)}|${Math.round(next.rect.width)}|${Math.round(next.rect.height)}` : '';
      if (key === last) return;
      last = key;
      setLiveSlide(next);
    };
    const obs = new MutationObserver(sync);
    try {
      obs.observe(doc.documentElement, { attributes: true, attributeFilter: ['class', 'style'], subtree: true, childList: true });
      win.addEventListener('resize', sync);
      sync();
    } catch {
      return;
    }
    return () => { obs.disconnect(); try { win.removeEventListener('resize', sync); } catch {} };
  }, [iframeReady, contentDocAccessible, snapshotBlobUrl]);

  // Open at the reviewed content, not the page top (Fredrik, FAQ—indications
  // walk 2026-09-04: the FAQ tab sits six stacked tabs down). A row cycle's
  // sourceUrl fragment names its item; a page cycle has no fragment, so the
  // topmost anchored comment is where the review starts. Once per snapshot,
  // after the page's own scripts have had a moment to settle the layout.
  const initialScrollDone = useRef(false);
  useEffect(() => { initialScrollDone.current = false; }, [snapshotBlobUrl]);
  useEffect(() => {
    if (!iframeReady || initialScrollDone.current) return;
    const win = iframeRef.current?.contentWindow;
    const doc = iframeRef.current?.contentDocument;
    if (!win || !doc) return;
    const frag = (cycle.sourceUrl || '').split('#')[1] || '';
    let top: number | null = null;
    try {
      if (frag) {
        const el = doc.getElementById(frag);
        if (el) top = Math.max(0, Math.round(el.getBoundingClientRect().top + win.scrollY - 80));
      }
      if (top == null) {
        const ys = comments.filter(c => c.coordinates).map(c => c.coordinates!.y);
        if (ys.length) top = Math.max(0, Math.round(Math.min(...ys) - SNAPSHOT_CAPTURE_HEIGHT / 3));
      }
    } catch { return; }
    if (top == null) return;
    initialScrollDone.current = true;
    const target = top;
    const t = window.setTimeout(() => { try { win.scrollTo({ top: target, left: 0 }); } catch {} }, 300);
    return () => window.clearTimeout(t);
  }, [iframeReady, comments, cycle.sourceUrl]);

  const handleIframeLoad = () => setIframeReady(true);

  // Measure the column and derive the scale. Runs on mount, on column resize
  // (the rail collapsing is exactly this) and on window resize.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const measure = () => {
      const available = el.clientWidth || SNAPSHOT_CAPTURE_WIDTH;
      // Never scale ABOVE 1: enlarging a 1440px capture only blurs it.
      setStageScale(Math.min(1, available / SNAPSHOT_CAPTURE_WIDTH));
    };

    measure();
    // jsdom has no ResizeObserver — the initial measure still runs, which is
    // all a test needs.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [snapshotBlobUrl]);

  // ────────────────────────── Comment-mode interactions ──────────────────────────

  const enterCommentMode = () => {
    if (readOnly) return;
    setCommentMode(true);
    resetPending();
  };

  const exitCommentMode = () => {
    setCommentMode(false);
    resetPending();
  };

  const resetPending = () => {
    setPendingCoords(null);
    setPendingSection('');
    setDragStart(null);
    setDragRect(null);
    setIsDragging(false);
  };

  // Resolve the smallest block-ancestor for a list of (x, y) points and
  // concatenate their visible text. Used as a fallback when the Range API
  // yields no text (rectangle dragged over images / spacers / non-text areas)
  // and as the primary path for pins (single-point clicks).
  const collectAncestorText = useCallback((doc: Document, points: Array<[number, number]>): string => {
    const seen = new Set<Element>();
    const ordered: Element[] = [];
    for (const [px, py] of points) {
      const el = doc.elementFromPoint(px, py);
      if (!el) continue;
      const ancestor = findBlockAncestor(el);
      if (!ancestor || seen.has(ancestor)) continue;
      seen.add(ancestor);
      ordered.push(ancestor);
    }
    return ordered
      .map(el => {
        const tag = el.tagName.toLowerCase();
        if (tag === 'img' || tag === 'picture') return describeImage(el);
        return visibleText(el);
      })
      .filter(Boolean)
      .join('\n\n');
  }, []);

  // Given a rectangle, use the Range API to extract exactly the text the user
  // marked — not the whole block. The grid-of-ancestors fallback is kept for
  // pins and for rectangles dragged over non-text content (images / spacers).
  // Capped at 2000 chars.
  const extractSectionContent = useCallback((rect: { vx: number; vy: number; width: number; height: number }): string => {
    const iframe = iframeRef.current;
    if (!iframe) return '';
    try {
      const doc = iframe.contentDocument;
      if (!doc) return '';

      const isRect = rect.width > 0 && rect.height > 0;
      const xs = [rect.vx, rect.vx + rect.width / 2, rect.vx + rect.width];
      const ys = [rect.vy, rect.vy + rect.height / 2, rect.vy + rect.height];
      const grid: Array<[number, number]> = [];
      for (const y of ys) for (const x of xs) grid.push([x, y]);

      // Pin: keep the block-ancestor behavior so a single click still has
      // useful surrounding context.
      if (!isRect) {
        return collectAncestorText(doc, [[rect.vx, rect.vy]]).substring(0, 2000);
      }

      // Rectangle: try Range API first for precise selection.
      let selectedText = '';
      const tl = caretAt(doc, rect.vx, rect.vy);
      const br = caretAt(doc, rect.vx + rect.width, rect.vy + rect.height);
      if (tl && br) {
        try {
          const range = doc.createRange();
          // Caret order may not match drag order — try both directions.
          try {
            range.setStart(tl.node, tl.offset);
            range.setEnd(br.node, br.offset);
          } catch {
            range.setStart(br.node, br.offset);
            range.setEnd(tl.node, tl.offset);
          }
          // caretFromPoint snaps to the nearest caret — when the drag bottom
          // is just past the last line's baseline it lands at the start of
          // the next line, pulling that line into the range. Same mirror at
          // the top. Trim characters whose bounding rect sits outside the
          // dragged rectangle so we keep only what the user actually marked.
          trimRangeToRect(doc, range, rect);
          selectedText = (range.toString() || '').replace(/\s+/g, ' ').trim();
        } catch {
          // Range construction failed entirely — fall through to ancestor path.
        }
      }

      // Detect images intersecting the rectangle (Range API only surfaces text
      // nodes — image content has to come from elementFromPoint sampling).
      const images: string[] = [];
      const seenImg = new Set<Element>();
      for (const [px, py] of grid) {
        const el = doc.elementFromPoint(px, py);
        if (!el) continue;
        let cur: Element | null = el;
        let depth = 0;
        while (cur && depth < 5) {
          const tag = cur.tagName.toLowerCase();
          if (tag === 'img' || tag === 'picture') {
            if (!seenImg.has(cur)) {
              seenImg.add(cur);
              images.push(describeImage(cur));
            }
            break;
          }
          cur = cur.parentElement;
          depth++;
        }
      }

      // If Range gave us text, use it (plus any images). Otherwise fall back
      // to the ancestor walk so we always provide some context.
      if (selectedText) {
        const combined = [selectedText, ...images].filter(Boolean).join('\n\n');
        return combined.substring(0, 2000);
      }

      const fallback = collectAncestorText(doc, grid);
      const combinedFallback = [fallback, ...images.filter(i => !fallback.includes(i))].filter(Boolean).join('\n\n');
      return combinedFallback.substring(0, 2000);
    } catch {
      return '';
    }
  }, [collectAncestorText]);

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    if (!commentMode || !canAuthor) return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    e.preventDefault();
    // The overlay lives inside the scaled stage, so its client rect is in
    // SCREEN pixels while everything stored is in stage pixels. Dividing here
    // is the single conversion point — extractSectionContent, elementFromPoint
    // and every saved coordinate stay in the snapshot's own coordinate space.
    const rect = overlay.getBoundingClientRect();
    const vx = (e.clientX - rect.left) / stageScale;
    const vy = (e.clientY - rect.top) / stageScale;
    setDragStart({ vx, vy });
    setDragRect({ vx, vy, width: 0, height: 0 });
    setIsDragging(true);
    setPendingCoords(null);
    setPendingSection('');
  };

  const handleOverlayMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart || !overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const cvx = (e.clientX - rect.left) / stageScale;
    const cvy = (e.clientY - rect.top) / stageScale;
    setDragRect({
      vx: Math.min(dragStart.vx, cvx),
      vy: Math.min(dragStart.vy, cvy),
      width: Math.abs(cvx - dragStart.vx),
      height: Math.abs(cvy - dragStart.vy)
    });
  };

  const handleOverlayMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (dragRect && dragRect.width > 10 && dragRect.height > 10) {
      // Valid rectangle — store in CONTENT coords
      const contentCoords: Coordinates = {
        x: dragRect.vx + iframeScroll.x,
        y: dragRect.vy + iframeScroll.y,
        width: dragRect.width,
        height: dragRect.height
      };
      setPendingCoords(contentCoords);
      setPendingSection(extractSectionContent(dragRect));
    } else if (dragStart) {
      // Too small — treat as a pin
      const contentCoords: Coordinates = {
        x: dragStart.vx + iframeScroll.x,
        y: dragStart.vy + iframeScroll.y
      };
      setPendingCoords(contentCoords);
      setPendingSection(extractSectionContent({ vx: dragStart.vx, vy: dragStart.vy, width: 0, height: 0 }));
    }
    setDragStart(null);
    setDragRect(null);
  };

  const handleSubmitComment = async (sub: ComposerSubmission) => {
    if (!sub.issue.trim() || !pendingCoords) return;
    try {
      setSubmitting(true);
      setSubmitError(null);
      const headers = await getAuthHeaders();
      // kind chosen in CommentComposer (spec §8) — consultations carry no
      // severity/suggestedFix.
      const body: Record<string, any> = {
        kind: sub.kind,
        issue: sub.issue.trim(),
        ...(sub.kind === 'finding'
          ? { severity: sub.severity, suggestedFix: (sub.suggestedFix || '').trim() }
          : {}),
        coordinates: pendingCoords,
        sectionType: 'dom_section',
        sectionId: `snapshot-${Math.round(pendingCoords.x)}-${Math.round(pendingCoords.y)}`
      };
      if (pendingSection) body.sectionContent = pendingSection;

      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/comments`,
        body,
        { headers }
      );

      onCommentAdded();
      // Stay in comment mode so reviewer can immediately mark the next issue
      resetPending();
    } catch (err) {
      console.error('Error submitting snapshot comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────── Visibility + marker rendering ──────────────────────────

  const visibleComments = useMemo(() => {
    // While a presentation is open, only the findings on the slide that is
    // showing are drawn — the rest of the page is under the modal.
    const list = comments.filter(c => c.coordinates && (!liveSlide || isOnLiveSlide(c, liveSlide)));
    if (commentVisibility === 'all') return list;
    if (commentVisibility === 'others') return list.filter(c => c.reviewer.email !== user?.email);
    if (commentVisibility === 'mine') return list.filter(c => c.reviewer.email === user?.email);
    // Legacy fallback
    return readOnly ? list : list.filter(c => c.reviewer.email === user?.email);
  }, [comments, commentVisibility, user?.email, readOnly, liveSlide]);

  // ── Comment anchors (2026-08-26) ──────────────────────────────────────
  //
  // The snapshot cannot use the DOM adapter: its markers are absolutely
  // positioned in an overlay that always exists, so "is the element on
  // screen" is the wrong question — the page scrolls INSIDE the iframe, and
  // the coordinate space the comment was saved in is the iframe document's.
  // Both answers are therefore arithmetic on coordinates we already track,
  // which is also why this is the cheapest of the adapters.
  const anchors = useMemo<CommentAnchorApi>(() => {
    const withCoords = comments.filter(c => c.coordinates);
    const byId = new Map(withCoords.map(c => [c._id, c] as const));

    return {
      reveal: (id: string) => {
        const c = byId.get(id);
        const win = iframeRef.current?.contentWindow;
        if (!c?.coordinates || !win) return false;
        const slide = c.coordinates.slide;
        if (slide) {
          // A slide finding: open its presentation (the page's own card
          // handler mounts the carousel) and step to the slide via its dot.
          // The observer above redraws the marker once the DOM settles.
          const doc = iframeRef.current?.contentDocument;
          const card = doc?.getElementById(slide.widget) as HTMLElement | null;
          const root = card?.closest('[data-media-cards]');
          if (card && root) {
            const open = root.querySelector('[class*="__modal"].is-open');
            const title = (open?.querySelector('[id^="mc-title-"]')?.textContent || '').trim();
            if (!open || title !== (card.dataset.carouselTitle || '').trim()) {
              (open?.querySelector('[class*="__modal-close"]') as HTMLElement | null)?.click();
              card.click();
            }
            window.setTimeout(() => {
              const modal = root.querySelector('[class*="__modal"].is-open');
              (modal?.querySelector(`[data-carousel-dot="${slide.index}"]`) as HTMLElement | null)?.click();
              window.setTimeout(() => {
                const el = overlayMarkerRef.current?.querySelector(`[data-comment-id="${id}"]`);
                if (el) flashAnchor(el);
              }, 250);
            }, 150);
            return true;
          }
        }
        // A third of the way down rather than centred: the marker is usually
        // the TOP of the passage it refers to, and the reader wants what
        // follows it.
        const top = Math.max(0, Math.round(c.coordinates.y - SNAPSHOT_CAPTURE_HEIGHT / 3));
        try {
          win.scrollTo({ top, left: 0, behavior: 'smooth' });
        } catch {
          return false;   // cross-origin frame — nothing we can do from here
        }
        // Let the smooth scroll settle before flashing, or the ring animates
        // while the marker is still travelling across the screen.
        window.setTimeout(() => {
          const el = overlayMarkerRef.current?.querySelector(`[data-comment-id="${id}"]`);
          if (el) flashAnchor(el);
        }, 320);
        return true;
      },
      /**
       * The READING BAND, not the whole viewport (2026-08-26, Fredrik).
       *
       * This used to return every marker anywhere in the 900px capture
       * viewport, so on a normal page most comments were "visible" at once
       * and each took its turn as the reading position while you scrolled —
       * "any paragraph gets highlighted", exactly. The band is the same
       * fraction of the viewport the DOM adapter observes; the two measure
       * differently (arithmetic here, IntersectionObserver there) and must
       * still agree on where a reader is looking.
       */
      visibleCommentIds: withCoords
        .filter(c => {
          // An open presentation IS the reading position: its slide's
          // findings, nothing under the modal.
          if (liveSlide) return isOnLiveSlide(c, liveSlide);
          const y = c.coordinates!.y;
          const bottom = y + (c.coordinates!.height || 0);
          const bandTop = iframeScroll.y + SNAPSHOT_CAPTURE_HEIGHT * READING_BAND.top;
          const bandBottom = iframeScroll.y + SNAPSHOT_CAPTURE_HEIGHT * READING_BAND.bottom;
          return bottom >= bandTop && y <= bandBottom;
        })
        .map(c => c._id),
      // Reading order on a page is top-to-bottom, then left-to-right for two
      // markers at the same height — the same order the eye takes them in.
      // A copy first: sort() mutates, and `withCoords` is read above.
      orderedCommentIds: [...withCoords]
        .sort((a, b) =>
          (a.coordinates!.y - b.coordinates!.y) || (a.coordinates!.x - b.coordinates!.x)
          || ((a.coordinates!.slide?.index ?? -1) - (b.coordinates!.slide?.index ?? -1)))
        .map(c => c._id),
      // The overlay owns the markers here, so that is the root to mark in —
      // not the content root, which on this path is a cross-origin iframe.
      // The same is true of hovering and of measuring: the marker the reader
      // can see is the overlay rectangle, not anything inside the frame.
      // State, then an effect below — never a class written straight onto the
      // marker. Same reason as the DOM adapter: an imperative write is applied
      // once and nothing puts it back, so any render that redraws the overlay
      // (a new comment, a state switch, a scroll that moves the markers) drops
      // it silently. Holding it as state also makes rail-hover light the rail
      // row, because both ends now read the same value.
      setActive: (id: string | null) => setActiveAnchorId(id),
      setHovered: (id: string | null) => setHoveredAnchorId(id),
      hoveredCommentId: hoveredAnchorId,
      anchorRect: (id: string) => {
        const el = overlayMarkerRef.current?.querySelector(`[data-comment-id="${id}"]`);
        return el ? el.getBoundingClientRect() : null;
      },
      hasAnchor: (id: string) => byId.has(id)
    };
  }, [comments, iframeScroll.y, hoveredAnchorId, liveSlide]);

  // The overlay is redrawn whenever the marker set or the scroll changes, so
  // both classes are re-applied against whatever is on screen now.
  useEffect(() => {
    applyActiveAnchor(overlayMarkerRef.current, activeAnchorId);
  }, [activeAnchorId, visibleComments, iframeScroll.y, liveSlide]);
  useEffect(() => {
    applyHoverAnchor(overlayMarkerRef.current, hoveredAnchorId);
  }, [hoveredAnchorId, visibleComments, iframeScroll.y, liveSlide]);

  // The rail's list. `visibleComments` above requires coordinates because a
  // marker cannot be drawn without them; a LIST has no such need, and a
  // finding that somehow arrived without coordinates would otherwise be
  // invisible on this path entirely.
  const railComments = useMemo(() => {
    const mine = (c: ReviewComment) => c.reviewer.email === user?.email;
    const byVisibility = commentVisibility === 'all' ? comments
      : commentVisibility === 'others' ? comments.filter(c => !mine(c))
      : commentVisibility === 'mine' ? comments.filter(mine)
      : comments;
    return byVisibility.filter(c => !c.parentComment && (c.kind || 'finding') !== 'consultation');
  }, [comments, commentVisibility, user?.email]);

  const handleMarkerHover = (e: React.MouseEvent, overlapping: ReviewComment[]) => {
    const containerRect = wrapperRef.current?.getBoundingClientRect();
    if (containerRect) {
      setHoveredComments({
        comments: overlapping,
        x: e.clientX - containerRect.left,
        y: e.clientY - containerRect.top
      });
    }
  };

  // ────────────────────────── Loading / error states ──────────────────────────

  if (polledStatus === 'pending') {
    return (
      <div style={messageBoxStyle('#fff8e1', '#856404')}>
        <strong>Capturing snapshot…</strong>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          The page is being saved as a self-contained HTML file so reviewers can navigate it
          inside Cellcohub. This typically takes about 60–90 seconds. The viewer will refresh
          automatically when it's ready.
        </p>
      </div>
    );
  }

  if (polledStatus === 'failed') {
    return (
      <div style={messageBoxStyle('#f8d7da', '#721c24')}>
        <strong>Snapshot capture failed</strong>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          The page couldn't be captured. Possible causes: page requires authentication,
          page didn't load within 60s, or the source URL is unreachable. Try resubmitting
          the cycle, or contact an admin to investigate.
        </p>
        {cycle.sourceUrl && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#888' }}>
            Source URL: <a href={cycle.sourceUrl} target="_blank" rel="noopener noreferrer">{cycle.sourceUrl}</a>
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div style={messageBoxStyle('#f8d7da', '#721c24')}>
        <strong>Couldn't load snapshot</strong>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>{error}</p>
        {/* 2026-08-19 (Fredrik): reloading the whole page was the only way out
            of this state. Retrying the fetch is enough — if the file is simply
            gone, the request itself triggers the server-side re-capture and
            the effect above switches us to the polling view. */}
        <button
          className="review-btn review-btn-outline"
          style={{ marginTop: 10, fontSize: 13 }}
          onClick={() => { setError(null); setReloadNonce(n => n + 1); }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!snapshotBlobUrl) {
    return <div style={messageBoxStyle('#f0f7ff', '#123250')}>Loading snapshot…</div>;
  }

  // ────────────────────────── Main render ──────────────────────────

  const hasPending = !!pendingCoords && canAuthor;

  return (
    <div className="document-viewer">
      {/* The "Open original page" link is the host's (DocumentViewer's
          snapshot branch renders it above the mention strip and tab bar);
          this component drew a second one under them — Fredrik, row:2
          walk 2026-09-04. */}

      {/* Comment-mode toolbar */}
      {canAuthor && (
        <div style={toolbarStyle}>
          {!commentMode ? (
            <>
              <button
                className="review-btn review-btn-primary"
                onClick={enterCommentMode}
                style={{ fontSize: 13 }}
                disabled={!iframeReady || !contentDocAccessible}
                title={!contentDocAccessible ? 'Cross-origin frame access blocked — commenting unavailable' : ''}
              >
                Add comment
              </button>
              <span style={{ fontSize: 12, color: '#666' }}>
                Browse the snapshot to find an issue, then click <em>Add comment</em> and drag a rectangle around it.
              </span>
            </>
          ) : (
            <>
              <button className="review-btn review-btn-outline" onClick={exitCommentMode} style={{ fontSize: 13 }}>
                Done commenting
              </button>
              <span style={{ fontSize: 12, color: '#123250' }}>
                <strong>Comment mode active.</strong> Drag a rectangle around the issue, or click to drop a pin.
              </span>
            </>
          )}
          {!contentDocAccessible && (
            <span style={{ fontSize: 11, color: '#856404', background: '#fff8e1', padding: '4px 8px', borderRadius: 4 }}>
              Iframe content access blocked — section context can't be captured.
            </span>
          )}
        </div>
      )}

      <CommentAnchorProvider value={anchors}>
      <div className="viewer-layout">
        {/* Iframe + overlays */}
        <div className="viewer-content">
          <div
            ref={wrapperRef}
            style={{ ...wrapperStyle, height: Math.round(SNAPSHOT_CAPTURE_HEIGHT * stageScale) }}
          >
            {/* Fixed-width stage — see SNAPSHOT_CAPTURE_WIDTH. Everything
                anchored to page coordinates lives in here and scales with it;
                the hover popover deliberately does NOT, so its text stays at
                full size and full sharpness. */}
            <div
              data-testid="snapshot-stage"
              style={{
                position: 'relative',
                width: SNAPSHOT_CAPTURE_WIDTH,
                height: SNAPSHOT_CAPTURE_HEIGHT,
                transform: `scale(${stageScale})`,
                transformOrigin: 'top left'
              }}
            >
              <iframe
                ref={iframeRef}
                title={`Snapshot of ${cycle.contentItem.name}`}
                src={snapshotBlobUrl}
                sandbox="allow-same-origin allow-scripts"
                onLoad={handleIframeLoad}
                style={iframeStyle}
              />

              {/* Marker overlay — always present, individual markers are interactive */}
              {/* Content -> rail hover, delegated on the overlay for the same
                  reason the DOM adapter delegates on the content root: two
                  marker shapes are drawn here (rect and pin) and a third
                  could be added, and a listener per renderer is how they
                  drift. `onMouseOver`/`onMouseOut` bubble; enter/leave do
                  not, so delegation needs these. */}
              <div
                ref={overlayMarkerRef}
                style={markerOverlayStyle}
                onMouseOver={e => {
                  const el = (e.target as Element)?.closest?.('[data-comment-id], [data-comment-ids]');
                  setHoveredAnchorId(el ? (idsOnElement(el)[0] || null) : null);
                }}
                onMouseOut={e => {
                  const to = e.relatedTarget;
                  const el = to instanceof Element
                    ? to.closest('[data-comment-id], [data-comment-ids]')
                    : null;
                  setHoveredAnchorId(el ? (idsOnElement(el)[0] || null) : null);
                }}
              >
                {visibleComments.map(c => {
                  const cx = c.coordinates!.x;
                  const cy = c.coordinates!.y;
                  // On an open slide the marker outlines the mounted slide
                  // itself (viewport coordinates — the modal is fixed);
                  // otherwise it sits at the stored page rectangle.
                  const onSlide = !!liveSlide && isOnLiveSlide(c, liveSlide);
                  const cw = onSlide ? liveSlide!.rect.width : (c.coordinates!.width || 0);
                  const ch = onSlide ? liveSlide!.rect.height : (c.coordinates!.height || 0);
                  const hasRect = cw > 0 && ch > 0;
                  const vx = onSlide ? liveSlide!.rect.x : cx - iframeScroll.x;
                  const vy = onSlide ? liveSlide!.rect.y : cy - iframeScroll.y;

                  // Cluster: find others that overlap this rect or are within 20px (pins)
                  const overlapping = visibleComments.filter(other => {
                    if (!other.coordinates) return false;
                    const ox = other.coordinates.x;
                    const oy = other.coordinates.y;
                    const ow = other.coordinates.width || 0;
                    const oh = other.coordinates.height || 0;
                    if (hasRect && ow && oh) {
                      return cx < ox + ow && cx + cw > ox && cy < oy + oh && cy + ch > oy;
                    }
                    return Math.abs(ox - cx) < 20 && Math.abs(oy - cy) < 20;
                  });
                  const isFirst = overlapping[0]?._id === c._id;
                  const count = overlapping.length;

                  // Mention axis + click-to-open (2026-08-20 rev 3).
                  //
                  // These landed in DocumentViewer first and were invisible in
                  // prod for three reported rounds, because THIS component draws
                  // the markers whenever the snapshot pipeline is on — which it
                  // is in prod (ENABLE_SNAPSHOT_MODE). DocumentViewer's own tab
                  // bar carries a warning from the last time a change hit only
                  // one of these two paths; this is the same trap, and the two
                  // marker renderers should be one component.
                  const { forMe, primaryId: clusterPrimary } = markerMentionProps(overlapping, user?.email);
                  const primaryId = clusterPrimary || c._id;

                  return hasRect ? (
                    <div
                      key={c._id}
                      data-comment-id={c._id}
                      data-comment-ids={overlapping.map(o => o._id).join(' ')}
                      className={`viewer-rect ${c.severity}${forMe ? ' mentions-you' : ''}`}
                      style={{
                        left: vx, top: vy, width: cw, height: ch,
                        pointerEvents: 'auto', cursor: 'pointer',
                        ...(forMe ? { borderColor: '#7c3aed', borderWidth: 3, background: 'rgba(124,58,237,0.14)' } : {})
                      }}
                      title={forMe ? 'You were mentioned here — click to open and reply' : 'Open this comment'}
                      onMouseEnter={(e) => handleMarkerHover(e, overlapping)}
                      onMouseLeave={() => setHoveredComments(null)}
                      onClick={() => openComment(primaryId)}
                    >
                      {isFirst && <span className="viewer-rect-count">{count}</span>}
                      {forMe && <span className="viewer-mention-flag" title="You were mentioned here">@ YOU</span>}
                    </div>
                  ) : (
                    <div
                      key={c._id}
                      data-comment-id={c._id}
                      data-comment-ids={overlapping.map(o => o._id).join(' ')}
                      className={`viewer-pin ${c.severity}${forMe ? ' mentions-you' : ''}`}
                      style={{
                        left: vx, top: vy, pointerEvents: 'auto', cursor: 'pointer',
                        ...(forMe ? { background: '#7c3aed', borderColor: '#5b21b6' } : {})
                      }}
                      title={forMe ? 'You were mentioned here — click to open and reply' : 'Open this comment'}
                      onMouseEnter={(e) => handleMarkerHover(e, overlapping)}
                      onMouseLeave={() => setHoveredComments(null)}
                      onClick={() => openComment(primaryId)}
                    >
                      {isFirst && count > 1 && <span className="viewer-pin-count">{count}</span>}
                      {forMe && <span className="viewer-mention-flag" title="You were mentioned here">@ YOU</span>}
                    </div>
                  );
                })}
              </div>

              {/* Drag-capture overlay — only present in comment mode */}
              {commentMode && canAuthor && (
                <div
                  ref={overlayRef}
                  style={dragOverlayStyle}
                  onMouseDown={handleOverlayMouseDown}
                  onMouseMove={handleOverlayMouseMove}
                  onMouseUp={handleOverlayMouseUp}
                  onMouseLeave={() => { if (isDragging) handleOverlayMouseUp(); }}
                >
                  {/* In-progress drag rectangle */}
                  {isDragging && dragRect && (
                    <div
                      className="viewer-rect active"
                      style={{
                        left: dragRect.vx, top: dragRect.vy,
                        width: dragRect.width, height: dragRect.height
                      }}
                    />
                  )}
                  {/* Pending rectangle (after release, before submit) */}
                  {!isDragging && pendingCoords && pendingCoords.width && pendingCoords.height && (
                    <div
                      className="viewer-rect active"
                      style={{
                        left: pendingCoords.x - iframeScroll.x,
                        top: pendingCoords.y - iframeScroll.y,
                        width: pendingCoords.width,
                        height: pendingCoords.height
                      }}
                    />
                  )}
                  {/* Pending pin (after release, before submit) */}
                  {!isDragging && pendingCoords && !pendingCoords.width && (
                    <div
                      className="viewer-pin active"
                      style={{
                        left: pendingCoords.x - iframeScroll.x,
                        top: pendingCoords.y - iframeScroll.y
                      }}
                    />
                  )}
                </div>
              )}

            </div>

            {/* Hover popover */}
            {hoveredComments && (
              <div className="viewer-comment-popover" style={{ left: hoveredComments.x, top: hoveredComments.y }}>
                {hoveredComments.comments.map(c => (
                  <div key={c._id} className="viewer-comment-popover-item">
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: 12 }}>{c.reviewer.name}</span>
                      <span className={`severity-badge ${c.severity}`} style={{ fontSize: 10, padding: '0 6px' }}>{c.severity}</span>
                    </div>
                    {c.sectionContent && (
                      <div style={{ fontSize: 11, color: '#777', fontStyle: 'italic', marginBottom: 2, maxHeight: 30, overflow: 'hidden' }}>
                        {c.sectionContent.substring(0, 80)}{c.sectionContent.length > 80 ? '…' : ''}
                      </div>
                    )}
                    <p style={{ margin: 0, fontSize: 12 }}>{c.issue}</p>
                    {c.suggestedFix && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#155724' }}>Fix: {c.suggestedFix}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ padding: '8px 0', fontSize: 11, color: '#6b7280' }}>
            Snapshot captured {cycle.updatedAt ? new Date(cycle.updatedAt).toLocaleString() : 'recently'}.
            Static at submission time — does not reflect later edits to the source page.
          </div>
        </div>

        {/* Right column — same rail component DocumentViewer renders, so the
            web path cannot drift from the file path again.

            UNCONDITIONAL as of 2026-08-26. It was `!readOnly`, and the reason
            given was that the rail's presence keeps the iframe width stable so
            saved markers stay anchored — but read-only is exactly where
            DecisionView and PreScreenView render, and `970c009a` had just
            moved consultations INTO this rail and deleted their own copies. A
            decision maker opening a web-page cycle — prod's main format — could
            no longer see a consultation at all, let alone answer one.

            The width argument no longer applies either: the stage is pinned to
            SNAPSHOT_CAPTURE_WIDTH and scaled, so what the column does to its
            width changes the zoom and nothing else. */}
        {(
          <ViewerSidePanel
            cycleId={String(cycle._id)}
            getAuthHeaders={getAuthHeaders}
            refreshKey={refreshKey}
            viewerEmail={user?.email}
            canEditAny={canEditAnyComment}
            comments={railComments}
            renderCommentDetail={renderCommentDetail}
            commentsHeader={commentsHeader}
            banner={submitError ? (
              <div data-testid="comment-submit-error" style={{
                margin: '0 0 8px', padding: '8px 10px', borderRadius: 6,
                background: '#f8d7da', border: '1px solid #f5c2c7', color: '#842029', fontSize: 12
              }}>
                {submitError}
              </div>
            ) : null}
            /* NULL when nothing is pending, not a guidance panel.
               This slot used to double as an info box ("N comments on this
               snapshot", "Click Add comment to start marking issues") because
               the composer WAS the right column — so it was never empty. With
               tabs, a permanently non-null composer meant the rail always
               showed a Comment tab, the appear/disappear transition that
               switches to it never fired, and a reviewer who dragged a
               rectangle got a pending marker on the page and no form to fill
               in: the composer sat behind an unselected tab. The toolbar above
               already carries the instructions, and the read-only note it used
               to show pointed at a Comment Summary that no longer exists. */
            composer={hasPending ? (
              <CommentComposer
                title={pendingCoords?.width && pendingCoords.height ? 'Comment on selected area' : 'Comment on pinned location'}
                quote={pendingSection || null}
                quoteBorderColor="#666"
                getAuthHeaders={getAuthHeaders}
                allowFinding={composerMode !== 'consultation'}
                submitting={submitting}
                onSubmit={handleSubmitComment}
                onCancel={resetPending}
              />
            ) : null}
          />
        )}
      </div>
      </CommentAnchorProvider>
    </div>
  );
};

// ────────────────────────── styles ──────────────────────────

const wrapperStyle: React.CSSProperties = {
  position: 'relative',
  // Height is set inline from the measured scale — the stage inside is
  // transformed, and a transform does not change layout size, so without it
  // the wrapper would reserve the stage's full unscaled height.
  width: '100%',
  background: '#fff',
  borderRadius: 8,
  overflow: 'hidden',
  border: '1px solid #e5e7eb'
};

const iframeStyle: React.CSSProperties = {
  display: 'block',
  // Fills the stage, which is 1440 wide by construction. The height used to
  // be `min(900px, 80vh)` here; it is now the stage's, because the stage
  // grows as it scales down so the on-screen box stays a constant size.
  width: '100%',
  height: '100%',
  border: 0
};

const markerOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden'
};

const dragOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  cursor: 'crosshair',
  userSelect: 'none',
  background: 'rgba(18, 50, 80, 0.04)'
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 12px',
  marginBottom: 10,
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  flexWrap: 'wrap'
};

function messageBoxStyle(bg: string, color: string): React.CSSProperties {
  return { padding: 16, borderRadius: 8, background: bg, color, fontSize: 14 };
}

export default SnapshotViewer;
